import { useState, type ReactNode } from 'react';

/**
 * HomeMedia — banda de imagen reutilizable para las cards del Home (eventos,
 * tienda, experiencias). Muestra la imagen REAL del recurso (`coverImage` /
 * `imageUrl`) con:
 *   - carga diferida (`loading="lazy"`) + `object-cover` (sin deformación),
 *   - fallback de marca (gradiente azul→violeta + ícono) cuando no hay imagen
 *     o falla la carga (`onError`),
 *   - velo inferior opcional para que un badge superpuesto se lea sobre
 *     cualquier foto.
 *
 * `children` se renderiza como overlay (posicionado por el consumidor). El
 * zoom sutil en hover se activa cuando la card padre tiene la clase `group`.
 */

function DefaultTicketGlyph(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
      <path d="M13 5v2" />
      <path d="M13 17v2" />
      <path d="M13 11v2" />
    </svg>
  );
}

interface HomeMediaProps {
  src: string | null;
  alt?: string;
  /** Tailwind aspect-ratio class. Default `aspect-[16/10]`. */
  aspect?: string;
  /** Ícono mostrado en el fallback de marca. Default: ticket. */
  fallbackIcon?: ReactNode;
  /** Velo inferior para legibilidad de overlays. Default true. */
  veil?: boolean;
  /** Overlay (badges, chips) posicionado en absoluto por el consumidor. */
  children?: ReactNode;
  className?: string;
}

export function HomeMedia({
  src,
  alt = '',
  aspect = 'aspect-[16/10]',
  fallbackIcon,
  veil = true,
  children,
  className = '',
}: HomeMediaProps): JSX.Element {
  const [ok, setOk] = useState(true);
  const showImage = Boolean(src) && ok;

  return (
    <div className={`relative w-full overflow-hidden bg-neutral-100 ${aspect} ${className}`.trim()}>
      {showImage ? (
        <img
          src={src as string}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setOk(false)}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
        />
      ) : (
        <div className="relative flex h-full w-full items-center justify-center bg-gradient-to-br from-primary-500 via-primary-700 to-[#3b1d8a] text-white/80">
          {/* Textura sutil (glow + grid) para que el fallback no se vea plano. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(70% 60% at 30% 20%, rgba(255,255,255,0.22) 0%, transparent 60%),' +
                'radial-gradient(60% 60% at 90% 100%, rgba(0,214,238,0.28) 0%, transparent 60%)',
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px),' +
                'linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />
          <span className="relative drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]">
            {fallbackIcon ?? <DefaultTicketGlyph />}
          </span>
        </div>
      )}
      {veil && (
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent"
        />
      )}
      {children}
    </div>
  );
}
